class ProductSwitcher {
  constructor(container) {
    this.container = container;
    if (!this.container || this.container.dataset.switcherInitialized) return;

    this.container.dataset.switcherInitialized = 'true';
    this.productInfo = this.container.closest('product-info');
    if (!this.productInfo) return;

    this.sectionId = this.productInfo.dataset.section;
    this.currentSectionUrl = this.buildSectionUrl(window.location.href);
    this.isTransitioning = false;
    this.currentAnimationId = 0;
    this.pendingClick = null;
    this._isScrolling = false;
    this._scrollTimeout = null;
    this._touchStartX = 0;
    this._touchStartY = 0;
    this._touchMoved = false;
    this._isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.zIndexApplied = false;
    this.judgeMeFetchController = null;
    this.init();
  }

  init() {
    this.updateAllLabelColors();
    this.updateBuyButtonColor();
    this.updatePageBackground();
    this.ensureContentZIndex();
    this.container.addEventListener('mouseover', (e) => {
      const item = e.target.closest('.product-switcher__item');
      if (item) this.preload(item.dataset.productUrl);
    }, { passive: true });

    window.addEventListener('scroll', () => {
      this._isScrolling = true;
      clearTimeout(this._scrollTimeout);
      this._scrollTimeout = setTimeout(() => { this._isScrolling = false; }, 100);
    }, { passive: true });

    // Touch devices: respond on touchend for immediate feedback (no click delay).
    // Track movement during the gesture to distinguish taps from scrolls.
    // touchmove only fires while the finger is on screen — not during iOS
    // momentum deceleration — so this accurately reflects intent.
    if (this._isTouchDevice) {
      this.container.addEventListener('touchstart', (e) => {
        const t = e.touches[0];
        this._touchStartX = t.clientX;
        this._touchStartY = t.clientY;
        this._touchMoved = false;
      }, { passive: true });

      this.container.addEventListener('touchmove', (e) => {
        if (this._touchMoved) return;
        const t = e.touches[0];
        const dx = Math.abs(t.clientX - this._touchStartX);
        const dy = Math.abs(t.clientY - this._touchStartY);
        if (dx > 8 || dy > 8) this._touchMoved = true;
      }, { passive: true });

      this.container.addEventListener('touchend', (e) => {
        if (this._touchMoved) return;
        const item = e.target.closest('.product-switcher__item');
        if (!item) return;
        // Prevent the browser from firing a click event after this touchend.
        e.preventDefault();
        if (item.classList.contains('is-active') || this.isTransitioning) return;
        this.executeTransition(item);
      }, { passive: false });
    }

    this.container.addEventListener('click', this.onItemClick.bind(this));
    this.warmProductCache();
  }

  warmProductCache() {
    const urls = Array.from(this.container.querySelectorAll('.product-switcher__item'))
      .map((item) => item.dataset.productUrl)
      .filter((url) => url && !PRODUCT_SWITCHER_PRELOADED_URLS.has(url));

    if (!urls.length) return;

    productSwitcherScheduleIdle(() => {
      urls.forEach((url) => this.preload(url));
    }, 900);
  }

  preload(url) {
    if (!url || PRODUCT_SWITCHER_PRELOADED_URLS.has(url)) return;
    PRODUCT_SWITCHER_PRELOADED_URLS.add(url);

    const sectionUrl = this.buildSectionUrl(url);
    this.fetchProductTemplate(sectionUrl, url).then((template) => {
      // Background preload: only the first image so we don't burn bandwidth
      if (template) this.preloadTemplateMedia(template, 1);
    });
    this.prefetchLink(url);
  }

  // Called immediately on click — loads the rest of the images (index 1+)
  // so they arrive while the transition animation is still playing.
  loadRemainingMedia(template) {
    if (!template) return;
    this.preloadTemplateMedia(template, 10, 1);
  }

  prefetchLink(url) {
    if (!url || PRODUCT_SWITCHER_PREFETCHED_LINKS.has(url)) return;
    PRODUCT_SWITCHER_PREFETCHED_LINKS.add(url);

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  }

  buildSectionUrl(url) {
    try {
      const sectionUrl = new URL(url, window.location.origin);
      if (this.sectionId) {
        sectionUrl.searchParams.set('section_id', this.sectionId);
      }
      return sectionUrl.toString();
    } catch (e) {
      return url;
    }
  }

  extractProductInfo(htmlText) {
    if (!htmlText) return null;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = htmlText;
    return (
      wrapper.querySelector(`product-info[data-section="${this.sectionId}"]`) ||
      wrapper.querySelector('product-info')
    );
  }

  fetchSectionHtml(sectionUrl) {
    const cached = PRODUCT_SWITCHER_SECTION_HTML_CACHE.get(sectionUrl);
    if (cached) return cached;

    const request = fetch(sectionUrl)
      .then((r) => (r.ok ? r.text() : null))
      .then((text) => {
        if (!text) PRODUCT_SWITCHER_SECTION_HTML_CACHE.delete(sectionUrl);
        return text;
      })
      .catch(() => {
        PRODUCT_SWITCHER_SECTION_HTML_CACHE.delete(sectionUrl);
        return null;
      });
    productSwitcherCacheSet(PRODUCT_SWITCHER_SECTION_HTML_CACHE, sectionUrl, request);
    return request;
  }

  fetchProductTemplate(sectionUrl, fallbackUrl) {
    const cachedTemplate = PRODUCT_SWITCHER_TEMPLATE_CACHE.get(sectionUrl);
    if (cachedTemplate) return cachedTemplate;

    const templatePromise = this.fetchSectionHtml(sectionUrl)
      .then((responseText) => {
        const fromSection = this.extractProductInfo(responseText);
        if (fromSection) return fromSection;

        if (!fallbackUrl) return null;
        return fetch(fallbackUrl)
          .then((r) => (r.ok ? r.text() : null))
          .then((fullText) => this.extractProductInfo(fullText))
          .catch(() => null);
      })
      .then((template) => {
        if (!template) PRODUCT_SWITCHER_TEMPLATE_CACHE.delete(sectionUrl);
        return template;
      })
      .catch(() => {
        PRODUCT_SWITCHER_TEMPLATE_CACHE.delete(sectionUrl);
        return null;
      });

    productSwitcherCacheSet(PRODUCT_SWITCHER_TEMPLATE_CACHE, sectionUrl, templatePromise);
    return templatePromise;
  }

  fetchProductInfo(url) {
    const sectionUrl = this.buildSectionUrl(url);
    return this.fetchProductTemplate(sectionUrl, url)
      .then((template) => (template ? template.cloneNode(true) : null))
      .catch(() => null);
  }

  resolveProductInfo(url, sectionUrl) {
    return this.fetchProductInfo(url).then((node) => {
      if (!node) return null;
      this.resetRuntimeState(node);
      return node;
    });
  }

  resetRuntimeState(root) {
    if (!root) return;
    root.removeAttribute('data-switcher-height-locked');
    root.style.height = '';
    root.style.minHeight = '';
    root.style.overflow = '';
    root.style.transition = '';
    root.style.opacity = '';
    root.style.transform = '';

    root.querySelectorAll('.product-switcher-group').forEach((group) => {
      group.removeAttribute('data-switcher-initialized');
    });

    root.querySelectorAll('.quantity-module').forEach((module) => {
      module.removeAttribute('data-qs-init');
    });
  }

  createNodeSnapshot(node) {
    if (!node) return null;
    const snapshot = node.cloneNode(true);
    this.resetRuntimeState(snapshot);
    return snapshot;
  }

  getActiveJudgeMeWidget(root = document) {
    if (!root || !root.querySelectorAll) return null;
    const widgets = Array.from(root.querySelectorAll('#judgeme_product_reviews'));
    if (!widgets.length) return null;

    const candidates = widgets.filter((widget) => {
      if (!widget || !widget.isConnected) return false;
      if (widget.closest('product-info')) return false;
      if (widget.closest('[data-sticky-buy-bar]')) return false;
      return true;
    });

    if (!candidates.length) return widgets[0];

    if (root !== document) return candidates[0];

    const visible = candidates.find((widget) => {
      const styles = window.getComputedStyle(widget);
      if (styles.display === 'none' || styles.visibility === 'hidden') return false;
      const rect = widget.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });

    return visible || candidates[0];
  }

  cleanupJudgeMeMarkup(root) {
    if (!root) return;
    root.style.display = 'block';
    root.style.visibility = 'visible';
    root.style.opacity = '1';
    root.removeAttribute('hidden');
    root.classList.add('jdgm--done-setup-widget');

    root.querySelectorAll('style.jdgm-temp-hiding-style').forEach((styleTag) => styleTag.remove());

    root.querySelectorAll('.jdgm-temp-hidden, .jdgm-hidden').forEach((node) => {
      node.classList.remove('jdgm-temp-hidden');
      node.classList.remove('jdgm-hidden');
      node.removeAttribute('hidden');
    });

    root.querySelectorAll('.jdgm-rev-widg, .jdgm-rev-widg__body, .jdgm-rev-widg__reviews, .jdgm-rev').forEach((node) => {
      node.style.display = '';
      node.style.visibility = '';
      node.style.opacity = '';
      node.style.transform = '';
      node.removeAttribute('hidden');
    });

    // Hard fallback: when Judge.me runtime doesn't initialize after AJAX swap,
    // legacy widget markup still contains pre-rendered reviews.
    root.querySelectorAll('.jdgm-legacy-widget-content').forEach((legacy) => {
      legacy.style.display = 'block';
      legacy.style.visibility = 'visible';
      legacy.style.opacity = '1';
      legacy.removeAttribute('hidden');
    });
  }

  callJudgeMeMethod(methodName) {
    if (!window.jdgm || typeof window.jdgm[methodName] !== 'function') return false;
    try {
      window.jdgm[methodName]();
      return true;
    } catch (e) {
      return false;
    }
  }

  logJudgeMe() {}

  initializeJudgeMeWidgetWithRetry(widget, transitionId) {
    if (!widget) return;

    const maxAttempts = 8;
    const delayMs = 250;
    let attempts = 0;

    const run = () => {
      if (transitionId !== this.currentAnimationId) return;
      if (!widget.isConnected) return;

      const initialized =
        this.callJudgeMeMethod('initializeWidgets') ||
        this.callJudgeMeMethod('loadWidgets') ||
        this.callJudgeMeMethod('init');

      this.callJudgeMeMethod('forceShowHideRevWidget');
      this.callJudgeMeMethod('visibleRevWidget');
      this.logJudgeMe('init_attempt', {
        attempt: attempts + 1,
        initialized,
        widgetDataId: widget.getAttribute('data-id') || null,
      });

      if (initialized || attempts >= maxAttempts) {
        const legacy = widget.querySelector('.jdgm-legacy-widget-content');
        const hasNonLegacyRenderedReviews = !!Array.from(
          widget.querySelectorAll('.jdgm-rev, .jdgm-rev-widg__summary, .jdgm-rev-widg__body')
        ).find((node) => !(legacy && legacy.contains(node)));

        if (!hasNonLegacyRenderedReviews && legacy) {
          legacy.style.display = '';
          legacy.style.visibility = '';
          legacy.removeAttribute('hidden');
        }
        this.logJudgeMe('init_complete', {
          initialized,
          attempts: attempts + 1,
          hasNonLegacyRenderedReviews,
          legacyShown: !!(legacy && !hasNonLegacyRenderedReviews),
          reviewCount: widget.querySelectorAll('.jdgm-rev').length,
          htmlLen: (widget.innerHTML || '').length,
        });
        return;
      }

      attempts += 1;
      setTimeout(run, delayMs);
    };

    run();
  }

  refreshJudgeMeReviewWidget(productUrl, transitionId) {
    if (!productUrl) return;

    const currentWidget = this.getActiveJudgeMeWidget(document);
    if (!currentWidget) {
      this.logJudgeMe('skip_no_current_widget', { productUrl, transitionId });
      return;
    }
    this.logJudgeMe('start', {
      productUrl,
      transitionId,
      currentAnimationId: this.currentAnimationId,
      currentDataId: currentWidget.getAttribute('data-id') || null,
      currentHtmlLen: (currentWidget.innerHTML || '').length,
    });

    // Cache hit: replay parsed widget HTML, skip the full-page fetch entirely.
    const cachedWidgetHtml = PRODUCT_SWITCHER_JUDGEME_WIDGET_CACHE.get(productUrl);
    if (cachedWidgetHtml) {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = cachedWidgetHtml;
      const cachedWidget = wrapper.firstElementChild;
      if (cachedWidget && transitionId === this.currentAnimationId) {
        this.cleanupJudgeMeMarkup(cachedWidget);
        const activeWidget = this.getActiveJudgeMeWidget(document);
        if (activeWidget) {
          activeWidget.replaceWith(cachedWidget);
          this.initializeJudgeMeWidgetWithRetry(cachedWidget, transitionId);
          this.logJudgeMe('cache_hit', { productUrl });
          return;
        }
      }
    }

    if (this.judgeMeFetchController) {
      try {
        this.judgeMeFetchController.abort();
        this.logJudgeMe('abort_previous_request');
      } catch (e) {
        // Ignore abort errors from previous request.
      }
    }

    const controller = new AbortController();
    this.judgeMeFetchController = controller;

    fetch(productUrl, { signal: controller.signal })
      .then((response) => {
        this.logJudgeMe('fetch_response', {
          ok: response.ok,
          status: response.status,
          url: response.url,
        });
        return response.ok ? response.text() : null;
      })
      .then((htmlText) => {
        if (!htmlText || controller.signal.aborted || transitionId !== this.currentAnimationId) {
          this.logJudgeMe('skip_after_fetch', {
            hasHtml: !!htmlText,
            aborted: controller.signal.aborted,
            transitionId,
            currentAnimationId: this.currentAnimationId,
          });
          return;
        }
        this.logJudgeMe('fetch_html_ready', { htmlLen: htmlText.length });

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');
        const sourceWidget = this.getActiveJudgeMeWidget(doc) || doc.getElementById('judgeme_product_reviews');
        if (!sourceWidget) {
          this.logJudgeMe('no_source_widget_in_response');
          return;
        }
        this.logJudgeMe('source_widget_found', {
          sourceDataId: sourceWidget.getAttribute('data-id') || null,
          sourceHtmlLen: (sourceWidget.innerHTML || '').length,
          sourceReviewCount: sourceWidget.querySelectorAll('.jdgm-rev').length,
        });

        productSwitcherCacheSet(
          PRODUCT_SWITCHER_JUDGEME_WIDGET_CACHE,
          productUrl,
          sourceWidget.outerHTML
        );

        const importedWidget = document.importNode(sourceWidget, true);
        this.cleanupJudgeMeMarkup(importedWidget);

        const activeWidget = this.getActiveJudgeMeWidget(document);
        if (!activeWidget || transitionId !== this.currentAnimationId) {
          this.logJudgeMe('skip_before_replace', {
            hasActiveWidget: !!activeWidget,
            transitionId,
            currentAnimationId: this.currentAnimationId,
          });
          return;
        }
        activeWidget.replaceWith(importedWidget);
        this.logJudgeMe('replaced_widget', {
          newDataId: importedWidget.getAttribute('data-id') || null,
          newHtmlLen: (importedWidget.innerHTML || '').length,
          newReviewCount: importedWidget.querySelectorAll('.jdgm-rev').length,
        });

        this.initializeJudgeMeWidgetWithRetry(importedWidget, transitionId);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') {
          this.logJudgeMe('fetch_aborted');
          return;
        }
        this.logJudgeMe('fetch_error', {
          message: (error && error.message) || String(error),
        });
        // Keep existing widget unchanged on fetch failure.
      })
      .finally(() => {
        if (this.judgeMeFetchController === controller) {
          this.judgeMeFetchController = null;
        }
        this.logJudgeMe('done');
      });
  }

  /**
   * Preload images from a template using <link rel="preload"> so the browser
   * can match srcset/sizes and avoid double-downloading when the real <img>
   * is inserted into the DOM.
   * @param {Element} template  - The product-info node to pull images from
   * @param {number}  limit     - Max images to preload (default 1 for background, 10 for on-click)
   * @param {number}  startAt   - Skip this many images before starting (default 0)
   */
  preloadTemplateMedia(template, limit = 1, startAt = 0) {
    if (!template) return;
    const images = Array.from(template.querySelectorAll('img[src], img[data-src]'));
    let preloaded = 0;

    for (let i = startAt; i < images.length; i++) {
      if (preloaded >= limit) break;
      const img = images[i];
      const src = img.getAttribute('src') || img.getAttribute('data-src');
      if (!src || PRODUCT_SWITCHER_PRELOADED_MEDIA.has(src)) continue;
      PRODUCT_SWITCHER_PRELOADED_MEDIA.add(src);

      // Use <link rel="preload"> with imagesrcset/imagesizes so the browser
      // picks the same URL it will use when the real <img> renders.
      // Falls back to a plain Image() if the API isn't supported.
      const srcset = img.getAttribute('srcset') || img.getAttribute('data-srcset');
      const sizes = img.getAttribute('sizes');

      if (srcset) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = src;
        link.setAttribute('imagesrcset', srcset);
        if (sizes) link.setAttribute('imagesizes', sizes);
        document.head.appendChild(link);
      } else {
        const preloadImg = new Image();
        preloadImg.decoding = 'async';
        preloadImg.src = src;
      }

      preloaded += 1;
    }
  }

  waitForMedia(root, timeoutMs = 400) {
    if (!root) return Promise.resolve();
    const images = Array.from(root.querySelectorAll('img')).filter((img) => !img.complete);
    if (!images.length) return Promise.resolve();

    const mediaReady = Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          })
      )
    );

    const timeout = new Promise((resolve) => setTimeout(resolve, timeoutMs));
    return Promise.race([mediaReady, timeout]);
  }

  lockHeight(node, height) {
    if (!node || !Number.isFinite(height) || height <= 0) return;
    node.dataset.switcherHeightLocked = 'true';
    node.style.minHeight = `${Math.ceil(height)}px`;
    node.style.height = `${Math.ceil(height)}px`;
    node.style.overflow = 'hidden';
  }

  releaseHeight(node) {
    if (!node || node.dataset.switcherHeightLocked !== 'true') return;

    // Snap the height to natural size in a single frame — no animated
    // height transition, which would shift all content below the section
    // for hundreds of milliseconds and look like a visible layout jump.
    requestAnimationFrame(() => {
      node.style.height = '';
      node.style.minHeight = '';
      node.style.overflow = '';
      node.style.transition = '';
      delete node.dataset.switcherHeightLocked;
    });
  }

  hoistStylesheets(node) {
    if (!node) return;

    // Move <link rel="stylesheet"> to <head> so styles persist across DOM swaps
    node.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || PRODUCT_SWITCHER_HOISTED_HREFS.has(href)) return;
      // First time seeing this href: confirm absence in head, then mark.
      // Subsequent calls hit the Set in O(1) and skip the DOM query.
      if (!document.head.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
        document.head.appendChild(link.cloneNode(true));
      }
      PRODUCT_SWITCHER_HOISTED_HREFS.add(href);
    });

    // Move <style> blocks to <head>, deduplicating by content via Set (O(1))
    node.querySelectorAll('style').forEach((style) => {
      const content = style.textContent;
      if (!content || PRODUCT_SWITCHER_HOISTED_STYLE_CONTENT.has(content)) return;
      document.head.appendChild(style.cloneNode(true));
      PRODUCT_SWITCHER_HOISTED_STYLE_CONTENT.add(content);
    });
  }

  swapWithoutFlash(currentNode, nextNode, onSwapped) {
    if (!currentNode || !nextNode) return;

    // Hoist stylesheets from both nodes to <head> before swapping,
    // so the browser never loses the CSS rules during the DOM replacement.
    this.hoistStylesheets(currentNode);
    this.hoistStylesheets(nextNode);

    // Remove old popup modals from the document body that were moved there
    // by the ModalDialog connectedCallback, so they don't block the new ones.
    if (this.sectionId) {
      document.querySelectorAll('body > modal-dialog:not(.media-modal)').forEach((modal) => {
        if (modal.dataset.section === this.sectionId) {
          modal.remove();
        }
      });
    }

    currentNode.replaceWith(nextNode);
    if (typeof onSwapped === 'function') onSwapped(nextNode);
  }

  reinitQuantityInputs(root) {
    if (!root) return;
    // cloneNode(true) runs the custom element constructor before children are
    // appended, so this.input is null and button listeners are never set up.
    // Fix: replace each quantity-input via insertAdjacentHTML so it is parsed
    // in the live document context — constructor runs after children are present.
    root.querySelectorAll('quantity-input').forEach((qInput) => {
      const html = qInput.outerHTML;
      qInput.insertAdjacentHTML('afterend', html);
      const fresh = qInput.nextElementSibling;
      qInput.remove();
      if (fresh && typeof fresh.validateQtyRules === 'function') {
        fresh.validateQtyRules();
      }
    });
  }

  ensureContentZIndex() {
    if (this.zIndexApplied || document.getElementById('product-switcher-styles')) return;
    this.zIndexApplied = true;
    
    const style = document.createElement('style');
    style.id = 'product-switcher-styles';
    style.textContent = `
      product-info,
      product-info > *,
      .page-width,
      .product,
      .product__media-wrapper,
      .product__info-wrapper,
      .product__info-container {
        position: relative;
      }
      .zoraw-header-wrapper:not(.transparent) {
        position: relative;
      }
    `;
    document.head.appendChild(style);
  }

  onItemClick(event) {
    // Touch devices are handled by the touchend listener; skip the click event
    // to avoid double-firing (touchend calls preventDefault on real taps).
    if (this._isTouchDevice) return;
    if (this._isScrolling) return;
    const item = event.target.closest('.product-switcher__item');
    if (!item) return;

    event.preventDefault();

    if (item.classList.contains('is-active') || this.isTransitioning) return;
    this.executeTransition(item);
  }

  executeTransition(item) {
    if (this.isTransitioning || !item || !item.dataset.productUrl) return;

    this.isTransitioning = true;
    this.pendingClick = null;
    const animationId = ++this.currentAnimationId;
    const url = item.dataset.productUrl;
    const targetSectionUrl = this.buildSectionUrl(url);
    const pageColor = item.dataset.pageColor;

    // Notify dependent sections (e.g. nutrition facts) immediately so they can
    // start their own fetches in parallel rather than waiting for product-info:loaded.
    document.dispatchEvent(new CustomEvent('product-info:switching', {
      bubbles: true,
      detail: { url: url }
    }));

    // On click: immediately kick off loading of the remaining images (3+)
    // that were skipped during the background preload. They'll arrive while
    // the swap animation is still playing, so by the time the user sees the
    // new product the images are already in the browser cache.
    const cachedTemplate = PRODUCT_SWITCHER_TEMPLATE_CACHE.get(targetSectionUrl);
    if (cachedTemplate) {
      Promise.resolve(cachedTemplate).then((template) => {
        this.loadRemainingMedia(template);
      });
    }

    const dataPromise = this.resolveProductInfo(url, targetSectionUrl);
    const currentProductInfo = this.productInfo;
    const currentHeight = currentProductInfo ? currentProductInfo.getBoundingClientRect().height : 0;

    // Instant background color change
    if (currentProductInfo) {
      if (!currentProductInfo.dataset.hardcodedBg) {
        currentProductInfo.style.backgroundColor = pageColor || '';
      }
      this.lockHeight(currentProductInfo, currentHeight);
    }

    dataPromise
      .then((newProductInfo) => {
        if (animationId !== this.currentAnimationId) return;

        let resolvedProductInfo = newProductInfo;
        if (!resolvedProductInfo) {
          console.warn('Product Switcher: unable to swap product content', url);
          this.releaseHeight(currentProductInfo);
          this.isTransitioning = false;
          this.pendingClick = null;
          return;
        }

        if (resolvedProductInfo === currentProductInfo) {
          this.releaseHeight(currentProductInfo);
          this.isTransitioning = false;
          this.pendingClick = null;
          return;
        }

        if (resolvedProductInfo.isConnected && resolvedProductInfo !== currentProductInfo) {
          resolvedProductInfo = resolvedProductInfo.cloneNode(true);
        }

        if (!resolvedProductInfo.dataset.hardcodedBg) {
          resolvedProductInfo.style.backgroundColor = pageColor || '';
        }
        this.lockHeight(resolvedProductInfo, currentHeight);

        const finalizeSwap = (node) => {
          this.productInfo = node;
          this.sectionId = node.dataset.section || this.sectionId;
          this.currentSectionUrl = targetSectionUrl;

          this.reinitQuantityInputs(node);
          this.ensureContentZIndex();
          node.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true, detail: { url: url } }));

          const newSwitcherGroup = node.querySelector('.product-switcher-group');
          if (newSwitcherGroup) {
            new ProductSwitcher(newSwitcherGroup);
          }

          window.history.pushState({}, '', url);

          // Defer Judge.me refresh to idle so it doesn't block the perceived swap.
          // Repeat visits hit the widget cache and skip the network entirely.
          productSwitcherScheduleIdle(() => {
            this.refreshJudgeMeReviewWidget(url, animationId);
          }, 250);

          this.waitForMedia(node).finally(() => {
            this.releaseHeight(node);
          });

          this.isTransitioning = false;
        };

        this.swapWithoutFlash(currentProductInfo, resolvedProductInfo, finalizeSwap);
      })
      .catch((e) => {
        console.error('Product Switcher Error:', e);
        this.releaseHeight(currentProductInfo);
        this.isTransitioning = false;
        this.pendingClick = null;
      });
  }

  updateBuyButtonColor() {
    const activeItem = this.container.querySelector('.product-switcher__item.is-active');
    if (!activeItem) return;

    const color = activeItem.dataset.customColor;
    const colorDarken = activeItem.dataset.colorDarken;
    const textColor = this.getContrastingTextColor(color);

    if (!this.productInfo) return;

    this.productInfo.style.setProperty('--p-button-background', color);
    this.productInfo.style.setProperty('--p-button-text', textColor);
    this.productInfo.style.setProperty('--p-button-background-hover', colorDarken);
    this.productInfo.style.setProperty('--p-button-shadow', color);
    this.productInfo.style.setProperty('--p-button-outline', color);
  }
  
  updatePageBackground() {
    const activeItem = this.container.querySelector('.product-switcher__item.is-active');
    if (!activeItem || !this.productInfo) return;
    
    const pageColor = activeItem.dataset.pageColor;
    if (!this.productInfo.dataset.hardcodedBg) {
      this.productInfo.style.backgroundColor = pageColor || '';
    }
  }

  updateAllLabelColors() {
    const items = this.container.querySelectorAll('.product-switcher__item');
    items.forEach(item => {
      const color = item.dataset.customColor;
      const textColor = this.getContrastingTextColor(color);
      const label = item.querySelector('.product-switcher__label');
      if (label) {
        label.style.setProperty('--switcher-item-color', color);
        label.style.setProperty('--switcher-item-text-color', textColor);
      }
    });
  }

  getContrastingTextColor(hex) {
    if (!hex || hex.length < 4) return '#000000';
    /* Expand shorthand #RGB to #RRGGBB */
    if (hex.length === 4) {
      hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }
    if (hex.length < 7) return '#000000';
    const r = parseInt(hex.substr(1, 2), 16);
    const g = parseInt(hex.substr(3, 2), 16);
    const b = parseInt(hex.substr(5, 2), 16);
    /* WCAG 2.0 relative luminance formula */
    const toLinear = (c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    /* Contrast ratio threshold: 4.5:1 for AA against white (L=1) and black (L=0) */
    return L > 0.179 ? '#000000' : '#FFFFFF';
  }
}

const PRODUCT_SWITCHER_SECTION_HTML_CACHE = new Map();
const PRODUCT_SWITCHER_TEMPLATE_CACHE = new Map();
const PRODUCT_SWITCHER_NODE_CACHE = new Map();
const PRODUCT_SWITCHER_PREFETCHED_LINKS = new Set();
const PRODUCT_SWITCHER_PRELOADED_MEDIA = new Set();
const PRODUCT_SWITCHER_PRELOADED_URLS = new Set();
const PRODUCT_SWITCHER_HOISTED_HREFS = new Set();
const PRODUCT_SWITCHER_HOISTED_STYLE_CONTENT = new Set();
const PRODUCT_SWITCHER_JUDGEME_WIDGET_CACHE = new Map();
const PRODUCT_SWITCHER_MAX_CACHE_SIZE = 30;

const productSwitcherScheduleIdle = (callback, timeout = 250) => {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(callback, { timeout });
  }
  return setTimeout(callback, 0);
};

/** Evicts the oldest entry when cache exceeds max size */
function productSwitcherCacheSet(map, key, value) {
  if (map.size >= PRODUCT_SWITCHER_MAX_CACHE_SIZE) {
    const oldest = map.keys().next().value;
    map.delete(oldest);
  }
  map.set(key, value);
}

const qsApplyDefaultStyles = (card) => {
  card.style.backgroundColor = card.style.getPropertyValue('--bg-default');
  card.style.color = card.style.getPropertyValue('--text-default');
  card.style.borderColor = card.style.getPropertyValue('--text-default');
};

const qsApplyActiveStyles = (card) => {
  card.style.backgroundColor = card.style.getPropertyValue('--bg-active');
  card.style.color = card.style.getPropertyValue('--text-active');
  card.style.borderColor = card.style.getPropertyValue('--text-active');
};

const initQuantityModules = (scope) => {
  const root = scope && scope.querySelectorAll ? scope : document;
  const containers = root.querySelectorAll('.quantity-module');
  if (!containers.length) return;

  containers.forEach((container) => {
    if (container.dataset.qsInit === 'true') return;
    container.dataset.qsInit = 'true';

    const cards = container.querySelectorAll('.qs-card');
    cards.forEach((card) => {
      if (card.classList.contains('active')) {
        qsApplyActiveStyles(card);
      } else {
        qsApplyDefaultStyles(card);
      }
    });
  });
};

document.addEventListener('click', (event) => {
  const card = event.target.closest('.qs-card');
  if (!card) return;
  const container = card.closest('.quantity-module');
  if (!container) return;

  const cards = container.querySelectorAll('.qs-card');
  cards.forEach((c) => {
    c.classList.remove('active');
    qsApplyDefaultStyles(c);
  });

  card.classList.add('active');
  qsApplyActiveStyles(card);

  const productInfo = container.closest('product-info');
  let input =
    (productInfo && productInfo.querySelector('input[name="quantity"][id^="Quantity-"]')) ||
    (productInfo && productInfo.querySelector('input[name="quantity"][form]')) ||
    (productInfo && productInfo.querySelector('input[name="quantity"]'));
  
  if (!input) {
    input =
      document.querySelector('input[name="quantity"][id^="Quantity-"]') ||
      document.querySelector('input[name="quantity"][form]') ||
      document.querySelector('input[name="quantity"]');
  }

  if (input) {
    input.value = card.dataset.quantity;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new Event('input', { bubbles: true }));
  } else {
    let hiddenInput = container.querySelector('input[name="quantity"]');
    if (!hiddenInput) {
      hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = 'quantity';
      
      let formId = null;
      if (productInfo && productInfo.dataset.section) {
        const form = productInfo.querySelector(`#product-form-${productInfo.dataset.section}`);
        if (form && form.id) formId = form.id;
      }
      if (!formId) {
         const fallbackForm =
          (productInfo && productInfo.querySelector('form[id^="product-form-"]')) ||
          document.querySelector('form[id^="product-form-"]');
         if (fallbackForm && fallbackForm.id) formId = fallbackForm.id;
      }

      if (formId) hiddenInput.setAttribute('form', formId);
      container.appendChild(hiddenInput);
    }
    hiddenInput.value = card.dataset.quantity;
  }
});

const initProductSwitcher = (container) => {
  const switcherGroups = container.querySelectorAll('.product-switcher-group');
  switcherGroups.forEach(group => {
    new ProductSwitcher(group);
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initProductSwitcher(document.body);
});

document.addEventListener('shopify:section:load', (event) => {
  initProductSwitcher(event.target);
});

document.addEventListener('product-info:loaded', (event) => {
  initQuantityModules(event.target);
});

document.addEventListener('shopify:section:load', (event) => {
  initQuantityModules(event.target);
});

(() => {
  const initStickyBar = (root) => {
    if (!root || root.nodeType !== 1) return;
    const bar = root.querySelector('[data-sticky-buy-bar]');
    if (!bar || bar.dataset.stickyInit) return;
    bar.dataset.stickyInit = 'true';
    const sectionId = bar.getAttribute('data-sticky-section');
    const existing = sectionId
      ? document.querySelector(`body > [data-sticky-buy-bar][data-sticky-section="${sectionId}"]`)
      : null;
    if (existing && existing !== bar) {
      existing.remove();
    }
    if (bar.parentElement !== document.body) {
      document.body.appendChild(bar);
    }
    const tryRenderJudgeMe = () => {
      if (window.jdgm && typeof window.jdgm.renderWidgets === 'function') {
        window.jdgm.renderWidgets(bar);
        return true;
      }
      return false;
    };
    if (!tryRenderJudgeMe()) {
      let attempts = 0;
      const retry = () => {
        if (tryRenderJudgeMe() || attempts >= 10) return;
        attempts += 1;
        setTimeout(retry, 500);
      };
      retry();
    }
    document.addEventListener('judgeme:loaded', tryRenderJudgeMe);
    document.addEventListener('jdgm:loaded', tryRenderJudgeMe);
    document.addEventListener('jdgm:ready', tryRenderJudgeMe);

    const submitButton =
      (sectionId && document.getElementById(`ProductSubmitButton-${sectionId}`)) ||
      root.querySelector('product-form .product-form__submit') ||
      root.querySelector('.product-form__submit');
    const barButton = bar.querySelector('[data-sticky-buy-button]');
    const barPrice = bar.querySelector('[data-sticky-buy-price]');
    const barLabel = bar.querySelector('[data-sticky-buy-label]');
    if (!submitButton || !barButton) return;

    const syncButton = () => {
      const label = submitButton.querySelector('span');
      if (label && label.textContent) {
        if (barLabel) {
          barLabel.textContent = label.textContent.trim();
        } else {
          barButton.textContent = label.textContent.trim();
        }
      }
      const isDisabled = submitButton.hasAttribute('disabled') || submitButton.getAttribute('aria-disabled') === 'true';
      barButton.toggleAttribute('disabled', isDisabled);
      barButton.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

      if (barPrice) {
        let priceText = '';
        const subscriptionRadioEl = root.querySelector('[data-purchase-type="subscription"]');
        const isSubscription = subscriptionRadioEl && subscriptionRadioEl.checked;

        if (isSubscription) {
          const subPrice = root.querySelector('[data-subscription-price]');
          if (subPrice && subPrice.textContent.trim()) {
            priceText = subPrice.textContent.trim();
          }
        }

        if (!priceText) {
          const priceContainer = root.querySelector(`#price-${root.dataset.section}`);
          if (priceContainer) {
            const sale = priceContainer.querySelector('.price-item--sale');
            const regular = priceContainer.querySelector('.price-item--regular');
            const current = sale || regular;
            if (current && current.textContent) priceText = current.textContent.trim();
          }
        }

        barPrice.textContent = priceText;
      }
    };

    const observer = new MutationObserver(syncButton);
    observer.observe(submitButton, { attributes: true, childList: true, subtree: true, characterData: true });

    const priceContainer = root.querySelector(`#price-${root.dataset.section}`);
    if (priceContainer) {
      const priceObserver = new MutationObserver(syncButton);
      priceObserver.observe(priceContainer, { childList: true, subtree: true, characterData: true });
    }

    root.querySelectorAll('[data-purchase-type]').forEach((radio) => {
      radio.addEventListener('change', syncButton);
    });

    const subPriceEl = root.querySelector('[data-subscription-price]');
    if (subPriceEl) {
      const subPriceObserver = new MutationObserver(syncButton);
      subPriceObserver.observe(subPriceEl, { childList: true, subtree: true, characterData: true });
    }

    syncButton();

    barButton.addEventListener('click', (event) => {
      event.preventDefault();
      submitButton.click();
    });

    let hasSeenButton = false;
    const updateVisibility = () => {
      const rect = submitButton.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (inView) hasSeenButton = true;
      const show = hasSeenButton && rect.bottom <= 0;
      if (show) {
        bar.classList.add('is-visible');
        bar.setAttribute('aria-hidden', 'false');
      } else {
        bar.classList.remove('is-visible');
        bar.setAttribute('aria-hidden', 'true');
      }
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        updateVisibility();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    updateVisibility();
  };

  const initAll = (scope) => {
    const root = scope || document;
    if (root.matches && root.matches('product-info')) {
      initStickyBar(root);
    }
    root.querySelectorAll('product-info').forEach(initStickyBar);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('product-info:loaded', (event) => initAll(event.target));
})();

/* Nutritional facts modal: handled natively by ModalDialog custom element (global.js).
   The close button has id="ModalClose-..." so the constructor wires up close, backdrop,
   and Escape key handlers. connectedCallback moves the modal to <body> so it renders
   above the header (escapes #MainContent z-index: 1 stacking context). */

(() => {
  const initPurchaseOptions = (scope) => {
    const root = scope && scope.querySelectorAll ? scope : document;
    const containers = root.querySelectorAll('[data-subscription-purchase-options]');
    if (!containers.length) return;

    containers.forEach((container) => {
      if (container.dataset.purchaseOptionsInit === 'true') return;
      container.dataset.purchaseOptionsInit = 'true';

      const oneTimeRadio = container.querySelector('[data-purchase-type="onetime"]');
      const subscriptionRadio = container.querySelector('[data-purchase-type="subscription"]');
      const oneTimeOption = oneTimeRadio ? oneTimeRadio.closest('.product-purchase-options__option') : null;
      const subscriptionOption = subscriptionRadio ? subscriptionRadio.closest('.product-purchase-options__option') : null;
      const planSelect = container.querySelector('[data-sub-plan-select]');
      const sellingPlanInput = container.querySelector('[data-selling-plan-input]');
      const oneTimePrice = container.querySelector('[data-one-time-price]');
      const subscriptionPrice = container.querySelector('[data-subscription-price]');
      const subscriptionSave = container.querySelector('[data-subscription-save]');
      const subscriptionPerBar = container.querySelector('[data-subscription-per-bar]');
      const formId = container.dataset.formId || '';

      const productInfoRoot = container.closest('product-info') || document;
      const mainPriceContainer = productInfoRoot.querySelector('[data-main-price-container]');
      const mainPriceOneTime = mainPriceContainer && mainPriceContainer.querySelector('[data-main-price-onetime]');
      const mainPriceSubscription = mainPriceContainer && mainPriceContainer.querySelector('[data-main-price-subscription]');
      const mainSubscriptionPrice = mainPriceContainer && mainPriceContainer.querySelector('[data-main-subscription-price]');
      const mainSubscriptionSave = mainPriceContainer && mainPriceContainer.querySelector('[data-main-subscription-save]');

      if (!oneTimeRadio || !subscriptionRadio || !planSelect || !sellingPlanInput) return;

      const parseCents = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      const getCurrencyCode = () => {
        const shopifyCurrency = window?.Shopify?.currency?.active;
        if (shopifyCurrency) return shopifyCurrency;

        const currencyText = document.querySelector('.currency-text')?.textContent || '';
        const matched = currencyText.match(/\b([A-Z]{3})\b/);
        return matched ? matched[1] : 'USD';
      };

      const formatMoneyCents = (cents) => {
        const safeCents = Number.isFinite(cents) ? cents : 0;
        const locale = document.documentElement.lang || navigator.language || 'en-US';
        const currencyCode = getCurrencyCode();
        const amount = safeCents / 100;

        try {
          const formatted = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
            currencyDisplay: 'narrowSymbol',
          }).format(amount);
          return `${formatted} ${currencyCode}`;
        } catch (e) {
          return `$${amount.toFixed(2)} ${currencyCode}`;
        }
      };

      const getQuantityInput = () => {
        const localRoot = container.closest('product-info') || document;

        if (formId) {
          const scoped = localRoot.querySelector(`input[name="quantity"][form="${formId}"]`);
          if (scoped) return scoped;

          const globalScoped = document.querySelector(`input[name="quantity"][form="${formId}"]`);
          if (globalScoped) return globalScoped;
        }

        return (
          localRoot.querySelector('input[name="quantity"]') ||
          document.querySelector('input[name="quantity"]')
        );
      };

      const getQuantity = () => {
        const quantityInput = getQuantityInput();
        const quantity = parseInt(quantityInput?.value || '1', 10);
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      };

      const updateOneTimePrice = () => {
        if (!oneTimePrice) return;
        const unitCents = parseCents(oneTimePrice.dataset.unitCents);
        if (unitCents === null) return;

        oneTimePrice.textContent = formatMoneyCents(unitCents);
      };

      const updateSubscriptionText = () => {
        const selectedOption = planSelect.options[planSelect.selectedIndex];
        if (!selectedOption) return;

        if (subscriptionPrice) {
          const selectedUnitCents = parseCents(selectedOption.dataset.priceCents);
          if (selectedUnitCents !== null) {
            subscriptionPrice.dataset.unitCents = String(selectedUnitCents);
          }

          const activeUnitCents = parseCents(subscriptionPrice.dataset.unitCents);
          if (activeUnitCents !== null) {
            subscriptionPrice.textContent = formatMoneyCents(activeUnitCents);
          } else if (selectedOption.dataset.price) {
            subscriptionPrice.textContent = selectedOption.dataset.price;
          }
        }

        if (subscriptionSave) {
          const saveText = selectedOption.dataset.saveText || '';
          subscriptionSave.textContent = saveText;
          subscriptionSave.classList.toggle('hidden', !saveText);
        }

        if (subscriptionPerBar) {
          const perBar = selectedOption.dataset.perBar || '';
          subscriptionPerBar.textContent = perBar ? `${perBar} / Bar` : '';
          subscriptionPerBar.classList.toggle('hidden', !perBar);
        }
      };

      const syncState = () => {
        const isSubscription = subscriptionRadio.checked;

        planSelect.disabled = !isSubscription;
        container.classList.toggle('is-subscription-selected', isSubscription);
        if (oneTimeOption) {
          oneTimeOption.classList.toggle('selected', !isSubscription);
          oneTimeOption.classList.toggle('is-selected', !isSubscription);
        }
        if (subscriptionOption) {
          subscriptionOption.classList.toggle('selected', isSubscription);
          subscriptionOption.classList.toggle('is-selected', isSubscription);
        }

        if (isSubscription && planSelect.value) {
          sellingPlanInput.disabled = false;
          sellingPlanInput.value = planSelect.value;
        } else {
          sellingPlanInput.value = '';
          sellingPlanInput.disabled = true;
        }

        updateOneTimePrice();
        updateSubscriptionText();

        if (mainPriceContainer) {
          if (mainPriceOneTime) {
            mainPriceOneTime.setAttribute('aria-hidden', isSubscription ? 'true' : 'false');
          }
          if (mainPriceSubscription) {
            mainPriceSubscription.setAttribute('aria-hidden', isSubscription ? 'false' : 'true');
          }
          if (mainSubscriptionPrice && subscriptionPrice) {
            mainSubscriptionPrice.textContent = subscriptionPrice.textContent;
          }
          if (mainSubscriptionSave) {
            const selectedOpt = planSelect.options[planSelect.selectedIndex];
            const saveText = selectedOpt ? (selectedOpt.dataset.saveText || '') : '';
            const match = saveText.match(/(\d+)\s*%/);
            mainSubscriptionSave.textContent = match ? `${match[1]}% Off` : '';
          }
        }
      };

      oneTimeRadio.addEventListener('change', syncState);
      subscriptionRadio.addEventListener('change', syncState);
      planSelect.addEventListener('change', syncState);

      const localRoot = container.closest('product-info') || document;
      const handleQuantityEvent = (event) => {
        const target = event.target;
        if (!target || target.name !== 'quantity') return;

        const targetFormId = target.getAttribute('form');
        if (formId && targetFormId && targetFormId !== formId) return;

        syncState();
      };

      localRoot.addEventListener('input', handleQuantityEvent);
      localRoot.addEventListener('change', handleQuantityEvent);
      localRoot.addEventListener('click', (event) => {
        if (!event.target.closest('.quantity__button')) return;
        setTimeout(syncState, 0);
      });

      syncState();
    });
  };

  const initAll = (scope) => {
    const root = scope || document;
    if (root.matches && root.matches('[data-subscription-purchase-options]')) {
      initPurchaseOptions(root);
    } else {
      initPurchaseOptions(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('product-info:loaded', (event) => initAll(event.target));
})();